/**
 * Cloud-mode picker for linking the project to one of the org's saved
 * asset origins (S3 / R2 / B2 / MinIO). Mirrors `DeployTargetPicker`
 * — same shape, different config field. The image-widget gallery
 * resolves through the linked origin server-side; the editor never
 * sees the access keys.
 */

import type { AssetOriginConfig } from '@bliksys/shared';
import { useEffect, useState } from 'react';

import { type AssetOriginSummary, orgApi, type OrgApi } from '../data/orgApi.js';

import { AssetOriginField, labelAssetKind, validateAssetOrigin } from './AssetOriginField.js';
import './DeployTargetPicker.css';

interface Props {
  orgSlug: string;
  value: string | null | undefined;
  onChange: (id: string | null) => void;
}

type AddState =
  | { kind: 'closed' }
  | {
      kind: 'open';
      draft: AssetOriginConfig | undefined;
      name: string;
      busy: boolean;
      error: string | null;
    };

export function AssetOriginPicker({ orgSlug, value, onChange }: Props): JSX.Element {
  const api: OrgApi = orgApi({ orgSlug });
  const [origins, setOrigins] = useState<AssetOriginSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addState, setAddState] = useState<AddState>({ kind: 'closed' });

  const refresh = async (): Promise<void> => {
    try {
      const list = await api.listAssetOrigins();
      setOrigins(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load asset origins.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug]);

  const handleAddOpen = (): void => {
    setAddState({ kind: 'open', draft: undefined, name: '', busy: false, error: null });
  };

  const handleAddSubmit = async (): Promise<void> => {
    if (addState.kind !== 'open') return;
    const trimmedName = addState.name.trim();
    if (!trimmedName) {
      setAddState({ ...addState, error: 'Name is required.' });
      return;
    }
    if (!addState.draft) {
      setAddState({ ...addState, error: 'Fill in the credentials and bucket fields.' });
      return;
    }
    const errors = validateAssetOrigin(addState.draft);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setAddState({ ...addState, error: firstError });
      return;
    }
    setAddState({ ...addState, busy: true, error: null });
    try {
      const created = await api.createAssetOrigin({ name: trimmedName, config: addState.draft });
      setAddState({ kind: 'closed' });
      await refresh();
      onChange(created.id);
    } catch (err) {
      setAddState({
        ...addState,
        busy: false,
        error: err instanceof Error ? err.message : 'Failed to create.',
      });
    }
  };

  return (
    <div className="deploy-target-picker">
      {loadError && (
        <p className="deploy-target-picker-error" role="alert">
          {loadError}
        </p>
      )}

      <label className="deploy-target-picker-label">
        <span>Linked asset origin</span>
        <select
          className="input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          disabled={origins === null}
        >
          <option value="">None</option>
          {(origins ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} · {labelAssetKind(o.kind)}
            </option>
          ))}
        </select>
      </label>

      {addState.kind === 'closed' ? (
        <button type="button" className="deploy-target-picker-add-toggle" onClick={handleAddOpen}>
          + Add new asset origin
        </button>
      ) : (
        <div className="deploy-target-picker-add">
          <label className="deploy-target-picker-label">
            <span>Name</span>
            <input
              type="text"
              className="input"
              value={addState.name}
              onChange={(e) => setAddState({ ...addState, name: e.target.value })}
              placeholder="e.g. R2 production bucket"
            />
          </label>
          <AssetOriginField
            origin={addState.draft}
            onChange={(draft) => setAddState({ ...addState, draft })}
          />
          {addState.error && (
            <p className="deploy-target-picker-error" role="alert">
              {addState.error}
            </p>
          )}
          <div className="deploy-target-picker-add-actions">
            <button
              type="button"
              className="landing-button-text"
              onClick={() => setAddState({ kind: 'closed' })}
              disabled={addState.busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="landing-button-primary"
              onClick={() => void handleAddSubmit()}
              disabled={addState.busy}
            >
              {addState.busy ? 'Saving…' : 'Save origin'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
